import { motion, useReducedMotion } from 'motion/react'
import {
	type ComponentProps,
	type ElementType,
	useEffect,
	useState
} from 'react'
import { cn } from '#lib/utils'

/*
 * Aceternity UI hover border gradient (ui.aceternity.com/components/hover-border-gradient),
 * adapted: a brand-red light travels the border of a primary button instead of
 * the demo's white-on-black with a blue glow, sized like the repo's `sm`
 * button, and it holds still under reduced motion.
 */

const DIRECTIONS = ['TOP', 'LEFT', 'BOTTOM', 'RIGHT'] as const

const MOVING = {
	TOP: 'radial-gradient(22% 60% at 50% 0%, var(--primary) 0%, transparent 100%)',
	LEFT: 'radial-gradient(18% 50% at 0% 50%, var(--primary) 0%, transparent 100%)',
	BOTTOM: 'radial-gradient(22% 60% at 50% 100%, var(--primary) 0%, transparent 100%)',
	RIGHT: 'radial-gradient(18% 50% at 100% 50%, var(--primary) 0%, transparent 100%)'
} satisfies Record<(typeof DIRECTIONS)[number], string>

const HIGHLIGHT =
	'radial-gradient(75% 180% at 50% 50%, var(--primary) 0%, transparent 100%)'

export function HoverBorderGradient(
	props: ComponentProps<'button'> & {
		as?: ElementType
		containerClassName?: string
		duration?: number
		clockwise?: boolean
	}
) {
	const {
		as: Tag = 'button',
		children,
		className,
		containerClassName,
		duration = 1,
		clockwise = true,
		...rest
	} = props
	const [hovered, setHovered] = useState(false)
	const [direction, setDirection] = useState(0)
	const reduceMotion = useReducedMotion()

	useEffect(() => {
		if (hovered || reduceMotion) {
			return
		}
		const interval = setInterval(() => {
			setDirection(
				(current) =>
					(current + (clockwise ? DIRECTIONS.length - 1 : 1)) %
					DIRECTIONS.length
			)
		}, duration * 1000)
		return () => clearInterval(interval)
	}, [hovered, reduceMotion, clockwise, duration])

	const moving = MOVING[DIRECTIONS[direction] ?? 'TOP']

	return (
		<Tag
			className={cn(
				'group/hbg relative flex h-min w-fit shrink-0 items-center justify-center overflow-visible rounded-md bg-primary/15 p-px outline-none transition duration-500 hover:bg-primary/25 focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50',
				containerClassName
			)}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			{...rest}
		>
			<span
				className={cn(
					'relative z-10 inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-[calc(var(--radius-md)-1px)] bg-primary px-3 font-medium text-primary-foreground text-sm transition-transform group-active/hbg:translate-y-px [&_svg]:size-4 [&_svg]:shrink-0',
					className
				)}
			>
				{children}
			</span>
			<motion.span
				animate={{ background: hovered ? [moving, HIGHLIGHT] : moving }}
				aria-hidden='true'
				className='absolute inset-0 z-0 overflow-hidden rounded-[inherit] blur-[2px]'
				initial={{ background: moving }}
				transition={{
					ease: 'linear',
					duration: reduceMotion ? 0 : duration
				}}
			/>
		</Tag>
	)
}
